import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import { ERROR_MESSAGE } from '../../../../../Components/helper';
import { getMyTodoList, createMyTodo, updateMyTodoStatus as updateMyTodoStatusApi, getMyTodoCount, deleteMyTodo as deleteMyTodoApi } from '../../../../../services/my-todo.service';
import {
  MyTodoActionTypes,
  MyTodoThunkAction,
  IMyTodo,
  IMyTodoListRow,
  MyTodoFilterValues,
} from './my-todo.types';
import { normalizeMyTodoListRows } from './my-todo-list-normalize';

/** API response body shape (axios interceptor returns response.data, not AxiosResponse) */
interface ApiResponseBody<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
}

/** Extract user-facing error message from axios error or API body */
function getErrorMessage(error?: unknown, apiBody?: unknown): string {
  if (error) {
    const err = error as { response?: { data?: { data?: { errMsg?: string }; message?: string } }; message?: string };
    return (
      err?.response?.data?.data?.errMsg ??
      err?.response?.data?.message ??
      err?.message ??
      ERROR_MESSAGE
    );
  }
  const body = apiBody as { data?: { errMsg?: string }; message?: string } | undefined;
  return body?.data?.errMsg ?? body?.message ?? ERROR_MESSAGE;
}

// Fetch MyTodos list
export const fetchMyTodos =
  (params: Partial<MyTodoFilterValues>): MyTodoThunkAction =>
  async (dispatch) => {
    const requestId = Date.now().toString();
    dispatch({ type: MyTodoActionTypes.SET_LOADING, payload: true });
    try {
      const response = (await getMyTodoList(params)) as ApiResponseBody<{
        doc?: IMyTodoListRow[];
        totalCount?: number;
      }>;
      const { code, data } = response;
      if (code === 200 && data) {
        const rawDoc = data.doc ?? [];
        const items = normalizeMyTodoListRows(rawDoc);
        await dispatch({
          type: MyTodoActionTypes.FETCH_MY_TODOS,
          payload: {
            items,
            totalCount: data.totalCount ?? 0,
            requestId,
            ...(params.isScrolled !== undefined ? { isScrolled: params.isScrolled } : {}),
          },
        });
        return true;
      }
      dispatch({ type: MyTodoActionTypes.SET_LOADING, payload: false });
      toasterMsg(getErrorMessage(undefined, response), 'danger');
      return false;
    } catch (error) {
      dispatch({ type: MyTodoActionTypes.SET_LOADING, payload: false });
      console.error('Error in fetchMyTodos:', error);
      toasterMsg(getErrorMessage(error), 'danger');
      return false;
    }
  };

// Add new MyTodo
export const addMyTodo =
  (todoData: Partial<IMyTodo>): MyTodoThunkAction =>
  async (dispatch) => {
    try {
      const response = (await createMyTodo(todoData)) as ApiResponseBody<IMyTodo>;
      if (response?.code === 200 || response?.code === 201) {
        await dispatch({
          type: MyTodoActionTypes.ADD_MY_TODO,
          payload: response.data as IMyTodo,
        });
        toasterMsg('Todo created successfully', 'success');
        return response.data;
      }
      toasterMsg(getErrorMessage(undefined, response), 'danger');
      throw new Error(getErrorMessage(undefined, response));
    } catch (error: unknown) {
      console.error('Error in addMyTodo:', error);
      toasterMsg(getErrorMessage(error), 'danger');
      throw error;
    }
  };

// Update MyTodo status
export const updateMyTodoStatus =
  (params: { id?: string; ids?: string[]; status: 'Pending' | 'Done'; updates?: Array<{ id: string; status: 'Pending' | 'Done' }> }): MyTodoThunkAction =>
  async (dispatch) => {
    try {
      const response = (await updateMyTodoStatusApi(params)) as ApiResponseBody;
      if (response?.code === 200) {
        if (params.id) {
          await dispatch({
            type: MyTodoActionTypes.UPDATE_MY_TODO_STATUS,
            payload: {
              id: params.id,
              status: params.status,
            },
          });
        }
        toasterMsg('Status updated successfully', 'success');
        return response.data;
      }
      toasterMsg(getErrorMessage(undefined, response), 'danger');
      throw new Error(getErrorMessage(undefined, response));
    } catch (error: unknown) {
      console.error('Error in updateMyTodoStatus:', error);
      toasterMsg(getErrorMessage(error), 'danger');
      throw error;
    }
  };

// Fetch MyTodo and All To-Do pending counts (session-scoped; both views in one response)
export const fetchMyTodoCount = (): MyTodoThunkAction => async (dispatch) => {
  try {
    const response = (await getMyTodoCount()) as ApiResponseBody<{
      myTodoCount?: number;
      allTodoCount?: number;
      count?: number;
    }>;
    if (response?.code === 200 && response.data) {
      const raw = response.data as {
        myTodoCount?: number;
        allTodoCount?: number;
        count?: number;
      };
      const pendingCount = raw.myTodoCount ?? raw.count ?? 0;
      const allTodoPendingCount = raw.allTodoCount ?? pendingCount;
      await dispatch({
        type: MyTodoActionTypes.FETCH_MY_TODO_COUNT,
        payload: { pendingCount, allTodoPendingCount },
      });
      return { pendingCount, allTodoPendingCount };
    }
    console.error('Error fetching todo count:', response?.data);
    return { pendingCount: 0, allTodoPendingCount: 0 };
  } catch (error) {
    console.error('Error in fetchMyTodoCount:', error);
    return { pendingCount: 0, allTodoPendingCount: 0 };
  }
};

// Delete MyTodo (single or bulk)
export const deleteMyTodo =
  (params: { id?: string; ids?: string[] }): MyTodoThunkAction =>
  async (dispatch) => {
    try {
      const response = (await deleteMyTodoApi(params)) as ApiResponseBody<{ deleted: number }>;
      if (response?.code === 200 || response?.code === 207) {
        await dispatch({
          type: MyTodoActionTypes.DELETE_MY_TODO,
          payload: params,
        });
        toasterMsg('Task deleted successfully', 'success');
        return response.data;
      }
      toasterMsg(getErrorMessage(undefined, response), 'danger');
      throw new Error(getErrorMessage(undefined, response));
    } catch (error: unknown) {
      console.error('Error in deleteMyTodo:', error);
      toasterMsg(getErrorMessage(error), 'danger');
      throw error;
    }
  };

// Set filter values
export const setFilterValues =
  (filterData: Partial<MyTodoFilterValues>): MyTodoThunkAction =>
  async (dispatch) => {
    try {
      await dispatch({
        type: MyTodoActionTypes.SET_FILTER_VALUES,
        payload: filterData,
      });
    } catch (error) {
      console.error('Error in setFilterValues:', error);
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

// Clear reducer
export const clearMyTodoReducer = (): MyTodoThunkAction => async (dispatch) => {
  try {
    await dispatch({
      type: MyTodoActionTypes.CLEAR_MY_TODO_REDUCER,
    });
  } catch (error) {
    console.error('Error in clearMyTodoReducer:', error);
  }
};
